import { useRef } from 'react';

import { Colors, Flex, Input, TextArea } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import {
  formatResidentRegistrationNumber,
  deformatCompanyTaxNumber,
} from '@revenuemarket/utils/format';
import { Form, InputRef, Typography } from 'antd';
import { FormInstance } from 'antd/lib/form/Form';

import { AddressModal } from '~components/ui/Modal/AddressModal';
import { COMMON_YUP } from '~constants/yup-scheme';
import { formatAddress, parseAddress } from '~utils/common/address';

const IndividualBusinessForm = ({
  form,
  sameBusinessRegExist,
  onFinish,
}: {
  form: FormInstance<any>;
  sameBusinessRegExist: boolean;
  onFinish: (values: any) => void;
}) => {
  const businessRegNumber = Form.useWatch('businessRegNumber', form) || '';

  // 주민등록번호 13자리 검증
  const isValidResidentNumber = businessRegNumber.replace(/\D/g, '').length === 13;
  const showResidentNumberError = businessRegNumber.length > 0 && !isValidResidentNumber;
  const detailAddressInputRef = useRef<InputRef>(null);

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      style={{
        marginTop: 26,
      }}
      validateTrigger="submit"
    >
      <Form.Item
        required
        name="name"
        label={
          <Flex align="center" gap={4}>
            <Typography.Text
              style={{
                color: Colors.gray800,
              }}
            >
              이름
            </Typography.Text>
          </Flex>
        }
      >
        <Input placeholder="" />
      </Form.Item>
      <Form.Item
        name="alias"
        label={
          <Flex align="center" gap={4}>
            <Typography.Text
              style={{
                color: Colors.gray800,
              }}
            >
              거래처명
            </Typography.Text>
          </Flex>
        }
      >
        <Input placeholder="이 거래처를 쉽게 알아볼 수 있도록 거래처 별칭을 입력해 주세요" />
      </Form.Item>
      <Form.Item
        name="businessRegNumber"
        label={
          <Flex align="center" gap={4}>
            <Typography.Text
              style={{
                color: Colors.gray800,
              }}
            >
              주민등록번호 (1회 식별 용도로만 사용되며, 저장되지 않습니다)
            </Typography.Text>
          </Flex>
        }
      >
        <Input
          formatter={formatResidentRegistrationNumber}
          deformatter={deformatCompanyTaxNumber}
          maxLength={14}
          inputStatus={showResidentNumberError || sameBusinessRegExist ? 'error' : undefined}
          additionalText={
            sameBusinessRegExist
              ? '이미 등록된 거래처입니다'
              : showResidentNumberError
                ? '주민등록번호 13자리를 입력해주세요'
                : ''
          }
        />
      </Form.Item>
      <Flex direction="column" style={{ marginBottom: 24 }}>
        <Flex gap={4} align="center">
          <Typography.Text style={{ color: Colors.gray800 }}>주소</Typography.Text>
        </Flex>
        <Form.Item
          name="address"
          required
          style={{
            marginBottom: 8,
          }}
        >
          <Input
            readOnly
            placeholder="주소"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              overlays.open(({ isOpen, onClose }) => (
                <AddressModal
                  isOpen={isOpen}
                  onClose={onClose}
                  onComplete={(address) => {
                    form.setFieldsValue({
                      address: formatAddress(parseAddress(address)),
                    });

                    onClose();
                    // WARNING: antd delays recover focus to form item input
                    setTimeout(() => {
                      detailAddressInputRef.current?.focus();
                    }, 500);
                  }}
                />
              ));
            }}
          />
        </Form.Item>
        <Form.Item name="lotAddress" noStyle>
          <Input ref={detailAddressInputRef} placeholder="상세 주소" />
        </Form.Item>
      </Flex>
      <Form.Item
        name="email"
        label={
          <Flex align="center" gap={4}>
            <Typography.Text style={{ color: Colors.gray800 }}>이메일</Typography.Text>
          </Flex>
        }
        rules={[
          {
            validator: async (_, value) => {
              if (!value) return Promise.resolve(); // 빈 값은 허용
              try {
                await COMMON_YUP.email.validate(value);
                return Promise.resolve();
              } catch (error: any) {
                return Promise.reject(error.message);
              }
            },
          },
        ]}
        hasFeedback={false} // 기본 피드백 아이콘 숨기기
      >
        <Input placeholder="이메일을 입력하세요" width="100%" />
      </Form.Item>
      <Form.Item
        name="memo"
        label={
          <Flex align="center" gap={4}>
            <Typography.Text
              style={{
                color: Colors.gray800,
              }}
            >
              메모
            </Typography.Text>
          </Flex>
        }
      >
        <TextArea placeholder="메모를 입력하세요" rows={4} />
      </Form.Item>
    </Form>
  );
};

export default IndividualBusinessForm;
